import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ocrImage } from '../services';

const OCRPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [prompt, setPrompt] = useState('');
  const [result, setResult] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setResult('');
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setResult('');
    if (!file) {
      setError(t('error:image_required', 'Please select an image to upload.'));
      return;
    }
    try {
      setLoading(true);
      const data = await ocrImage(file, i18n.language, prompt);
      setResult(data?.text || '');
    } catch (err: any) {
      const msg = err?.detail || err?.message || 'Failed to extract text.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(result);
    } catch {}
  };

  return (
    <div className="max-w-3xl mx-auto px-4">
      <h1 className="text-2xl font-bold mb-4">{t('ocr:title', 'Image Text Extractor')}</h1>

      <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2">{t('ocr:image', 'Upload Image')}</label>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="block w-full text-sm text-gray-700 dark:text-gray-200"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">{t('ocr:notes', 'Optional Focus Notes')}</label>
          <input
            type="text"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder={t('ocr:notes_placeholder', 'e.g., Extract Sanskrit verses only')}
            className="w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className={`px-4 py-2 rounded-md text-white ${loading ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'} transition`}
        >
          {loading ? t('ocr:processing', 'Processing...') : t('ocr:extract', 'Extract Text')}
        </button>

        {error && (
          <div className="text-red-600 text-sm">{error}</div>
        )}
      </form>

      {result && (
        <div className="mt-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">{t('ocr:result', 'Extracted Text')}</h2>
            <button onClick={handleCopy} className="text-sm bg-gray-100 dark:bg-gray-800 px-3 py-1 rounded-md">
              {t('ocr:copy', 'Copy')}
            </button>
          </div>
          <pre className="mt-2 whitespace-pre-wrap bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4 text-gray-800 dark:text-gray-100">
            {result}
          </pre>
        </div>
      )}
    </div>
  );
};

export default OCRPage;